import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  QrCode, Power, ListChecks, Inbox, CheckCircle2, Clock,
  Plus, Loader2, Copy, UploadCloud, Pencil, LayoutDashboard, Users, KeyRound,
  ShieldCheck, ShieldOff, Trash2, Banknote, RefreshCcw,
} from "lucide-react";
import { api, apiError, timeAgo, money, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { StatusPill, LiveIndicator } from "@/components/Pills";
import { DashboardCharts } from "@/components/DashboardCharts";
import { TwoFactorSettings } from "@/components/TwoFactorSettings";
import { DateRangePicker, iso as isoDate } from "@/components/DateRangePicker";
import { RelativeTime, exact as exactTime } from "@/components/RelativeTime";
import { ExpandableRow, payerItems } from "@/components/ExpandableRow";
import { ExtLoginOtpPopup } from "@/components/ExtLoginOtpPopup";
import { SettlementPanel } from "@/components/SettlementPanel";
import { ChargeBackPanel } from "@/components/ChargeBackPanel";
import { AccessControlPanel } from "@/components/AccessControlPanel";
import { QrRoutingPanel } from "@/components/QrRoutingPanel";
import { TableTools, useTableSearch } from "@/components/TableTools";
import { Shuffle, MessageSquare, KeyRound as PinIcon } from "lucide-react";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { useChatUnread } from "@/components/chat/useChatUnread";
import { PayinShare, ScreenshotThumb } from "@/components/chat/PayinShare";
import { clearChatToken } from "@/lib/chatSession";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const card = "rounded-xl border border-slate-200 bg-white shadow-sm";
const primaryBtn = "bg-teal-600 hover:bg-teal-700 text-white";
const outlineBtn = "border-slate-300 bg-white text-slate-700 hover:bg-slate-50";
const inputCls = "mt-1 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400";
const dialogCls = "bg-white border-slate-200 text-slate-900";
const fieldWell = "bg-slate-50 border border-slate-200 rounded";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isSuper = user?.role === "super_admin";
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [received, setReceived] = useState([]);
  const [customer, setCustomer] = useState([]);
  const [merchants, setMerchants] = useState([]);

  const loadAll = async () => {
    try {
      const reqs = [
        api.get("/branch/stats"), api.get("/businesses"), api.get("/received-payments"),
        api.get("/customer-payments"),
      ];
      if (isSuper) reqs.push(api.get("/merchants"));
      const [s, b, r, c, v] = await Promise.all(reqs);
      setStats(s.data); setBusinesses(b.data); setReceived(r.data);
      setCustomer(c.data); setMerchants(isSuper && v ? v.data : []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 5000);
    return () => clearInterval(t);
  }, []);

  const doLogout = async () => { const back = user?.role === "super_admin" ? "/super-admin/login" : "/branch/login"; clearChatToken(); await logout(); nav(back); };

  const statCards = [
    { k: "total_businesses", label: "Total QRs", icon: QrCode, accent: "text-indigo-500" },
    { k: "active_businesses", label: "Active QRs", icon: Power, accent: "text-teal-600" },
    { k: "received_entries", label: "Branch Payment Entries", icon: ListChecks, accent: "text-cyan-600" },
    { k: "customer_submissions", label: "Customer Submissions", icon: Inbox, accent: "text-sky-600" },
    { k: "matched_payments", label: "Matched Payments", icon: CheckCircle2, accent: "text-teal-600" },
    { k: "pending_payments", label: "Pending Payments", icon: Clock, accent: "text-amber-500" },
  ];

  const [section, setSection] = useState("overview");
  const chatUnread = useChatUnread(!!user, section === "chat");
  const NAV = [
    { key: "overview", label: "Overview", icon: LayoutDashboard, testid: "branch-nav-overview" },
    { key: "qr", label: "QR Management", icon: QrCode, testid: "branch-tab-businesses" },
    { key: "received", label: "Received Payments", icon: ListChecks, testid: "branch-tab-received-payments" },
    { key: "customer", label: "PayIn", icon: Inbox, testid: "branch-tab-customer-payments" },
    { key: "payout", label: "Payout", icon: Banknote, testid: "branch-tab-payouts" },
    { key: "settlement", label: "Settlement", icon: Banknote, testid: "branch-nav-settlement" },
    { key: "chargeback", label: "Charge Back", icon: RefreshCcw, testid: "branch-nav-chargeback" },
    { key: "chat", label: "Chat", icon: MessageSquare, testid: "nav-chat" },
    { key: "security", label: "Security", icon: ShieldCheck, testid: "branch-nav-security" },
    ...(isSuper ? [
      { key: "merchants", label: "Merchants", icon: Users, testid: "branch-tab-merchants" },
      { key: "settings", label: "Extension Access", icon: KeyRound, testid: "branch-nav-settings" },
      { key: "access", label: "Access Control", icon: ShieldOff, testid: "super-nav-access-control" },
      { key: "routing", label: "QR Routing", icon: Shuffle, testid: "super-nav-qr-routing" },
      { key: "branches", label: "Manage Branches", icon: ShieldCheck, testid: "branch-nav-branches" },
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA] text-[#1F2937] font-sans">
      {!isSuper && <ExtLoginOtpPopup />}
      <header className="border-b border-slate-200 sticky top-0 z-20 bg-white/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo />
            {isSuper && (
              <span data-testid="super-branch-badge" className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                <ShieldCheck className="w-3 h-3" /> Super Admin
              </span>
            )}
            <span className="hidden sm:inline text-xs text-slate-500 border-l border-slate-200 pl-3">{user?.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <LiveIndicator />
            <Link to="/" data-testid="nav-customer-page-link" className="hidden sm:inline-flex">
              <Button variant="outline" className={`${outlineBtn} h-9`}>Customer Page</Button>
            </Link>
            <Button data-testid="logout-button" onClick={doLogout} variant="outline" className={`${outlineBtn} h-9`}>Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 py-6 flex flex-col md:flex-row gap-6">
        <nav className="md:w-56 shrink-0">
          <div className="flex md:flex-col gap-1 overflow-x-auto pb-1 -mx-1 px-1">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = section === n.key;
              return (
                <button key={n.key} data-testid={n.testid} onClick={() => setSection(n.key)}
                  className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors shrink-0 md:w-full ${active ? "bg-teal-50 text-teal-700 border border-teal-200" : "text-slate-500 hover:text-slate-800 hover:bg-slate-100 border border-transparent"}`}>
                  <Icon className="w-4 h-4" /> {n.label}
                  {n.key === "chat" && chatUnread > 0 && <span data-testid="nav-chat-unread" className="ml-auto text-[10px] min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white flex items-center justify-center">{chatUnread}</span>}
                </button>
              );
            })}
          </div>
        </nav>

        <main className="flex-1 min-w-0">
          {section === "overview" && (
            <div>
              <h1 className="text-xl font-bold tracking-tight mb-1 text-slate-900">Overview</h1>
              <p className="text-sm text-slate-500 mb-4">Live snapshot of your payment reconciliation.</p>
              <div data-testid="overview-charts">
                <DashboardCharts theme="light" showExport />
              </div>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="overview-stat-cards">
                {statCards.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.k} data-testid={`branch-stat-${s.k}`} className={`${card} p-4 hover:border-teal-300 transition-colors`}>
                      <Icon className={`w-4 h-4 ${s.accent}`} />
                      <div className="text-2xl font-black font-mono mt-2 text-slate-900">{stats ? stats[s.k] : "—"}</div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mt-0.5">{s.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {section === "qr" && <QrTab businesses={businesses} merchants={merchants} reload={loadAll} isSuper={isSuper} />}
          {section === "received" && <ReceivedTab businesses={businesses} received={received} reload={loadAll} isSuper={isSuper} />}
          {section === "customer" && <CustomerTab rows={customer} branchOf={(s) => (businesses.find((b) => b.id === s.business_id) || {}).branch_name || (isSuper ? "-" : user?.name || user?.email)} />}
          {section === "chat" && <ChatPanel user={user} />}
          {section === "payout" && <PayoutTab isSuper={isSuper} />}
          {section === "settlement" && <SettlementPanel isSuper={isSuper} />}
          {section === "chargeback" && <ChargeBackPanel isSuper={isSuper} />}
          {section === "security" && (
            <div className="max-w-2xl">
              <h1 className="text-xl font-bold tracking-tight mb-1 text-slate-900">Security</h1>
              <p className="text-sm text-slate-500 mb-4">Protect your account with an authenticator app.</p>
              <TwoFactorSettings theme="light" />
            </div>
          )}
          {section === "merchants" && isSuper && <MerchantsTab merchants={merchants} reload={loadAll} />}
          {section === "settings" && isSuper && <SettingsTab />}
          {section === "access" && isSuper && <AccessControlPanel />}
          {section === "routing" && isSuper && <QrRoutingPanel />}
          {section === "branches" && isSuper && <BranchesTab meId={user?.id} />}
        </main>
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="text-left text-[11px] uppercase tracking-wider text-slate-400 font-semibold px-3 py-2">{children}</th>; }
function Td({ children, mono, ...rest }) { return <td {...rest} className={`px-3 py-2.5 text-sm align-middle text-slate-700 ${mono ? "font-mono text-xs" : ""}`}>{children}</td>; }

/* ---------------- QR Management ---------------- */
const QR_STATUS = {
  active: ["Active", "bg-teal-50 text-teal-700 border-teal-200"],
  inactive: ["Inactive", "bg-slate-100 text-slate-500 border-slate-200"],
  closed: ["Permanently Closed", "bg-rose-50 text-rose-600 border-rose-200"],
};
const limitVal = (v) => (String(v ?? "").trim() === "" ? null : parseFloat(v));
const badLimit = (v) => v !== null && (isNaN(v) || v <= 0);
const mobileError = (p) => {
  const v = String(p || "").replace(/[\s\-()]/g, "");
  if (!v) return "Mobile Number is required";
  if (!/^(\+?91)?[6-9]\d{9}$/.test(v)) return "Mobile Number must be a valid 10-digit Indian mobile number";
  return null;
};
const bankError = (acc, ifsc, name) => {
  if (!/^\d{6,18}$/.test(String(acc || "").replace(/\s+/g, ""))) return "Account Number is required (6-18 digits)";
  if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(ifsc || "").trim().toUpperCase())) return "IFSC Code must be 11 characters, e.g. HDFC0001234";
  if (!String(name || "").trim()) return "Bank Name is required";
  return null;
};
const qrDetails = (b, st) => [
  ["QR name", b.name], ["Business / Account name", b.google_business_name], ["Bank name", b.bank_name],
  ["Account number", b.bank_account_number], ["IFSC", b.bank_ifsc], ["UPI ID", b.upi_id],
  ["Mobile", b.google_business_phone], ["Email", b.google_account_email],
  ["Amount range", `${money(b.amount_min)} – ${money(b.amount_max)}`],
  ["Daily limit", b.daily_limit != null ? money(b.daily_limit) : null], ["Lifetime limit", b.lifetime_limit != null ? money(b.lifetime_limit) : null],
  ["Status", QR_STATUS[st][0]], ["Created", b.created_at && exactTime(b.created_at)],
];

function UsageBar({ used, limit, testid }) {
  if (limit == null) return <div data-testid={testid} className="text-[11px] font-mono text-slate-500 whitespace-nowrap">{money(used)} / No limit</div>;
  const p = Math.min(100, (used / (limit || 1)) * 100);
  const red = p >= 90;
  return (
    <div className="w-32" data-testid={testid}>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${red ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: p + "%" }} /></div>
      <div className={`text-[10px] mt-1 font-mono ${red ? "text-rose-600 font-semibold" : "text-slate-500"}`}>{money(used)} / {money(limit)}</div>
    </div>
  );
}

function QrTab({ businesses, merchants, reload, isSuper }) {
  const canWrite = !isSuper;
  const empty = { name: "", upi_id: "", qr_image: "", amount_min: "1", amount_max: "100000", merchant_ids: [], google_account_email: "", google_business_name: "", google_business_phone: "", payment_provider: "", daily_limit: "", lifetime_limit: "", bank_account_number: "", bank_ifsc: "", bank_name: "" };
  const [statusFilter, setStatusFilter] = useState("active");
  const [closeTarget, setCloseTarget] = useState(null);
  const [closePw, setClosePw] = useState("");
  const [closeErr, setCloseErr] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editBiz, setEditBiz] = useState(null);
  const fileRef = useRef();

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/branch/upload-image", fd);
      setForm((p) => ({ ...p, qr_image: data.url, upi_id: data.upi_id || p.upi_id }));
      toast.success(data.upi_id ? "QR uploaded — UPI ID auto-filled" : "QR image uploaded");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const create = async () => {
    if (!form.name) return toast.error("QR name is required");
    if (!form.qr_image) return toast.error("QR image is required. Please upload the QR image");
    if (!(form.upi_id || "").trim()) return toast.error("UPI ID is required");
    if (!/^[A-Za-z0-9._-]{2,}@[A-Za-z0-9.-]{2,}$/.test(form.upi_id.trim())) return toast.error("UPI ID is not valid (e.g. name@okaxis)");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((form.google_account_email || "").trim())) return toast.error("Email is required and must be valid");
    const mErr = mobileError(form.google_business_phone);
    if (mErr) return toast.error(mErr);
    if (!(form.google_business_name || "").trim()) return toast.error("Business Name / Account Name is required");
    const bErr = bankError(form.bank_account_number, form.bank_ifsc, form.bank_name);
    if (bErr) return toast.error(bErr);
    if (!form.payment_provider) return toast.error("Please select a Payment App");
    const amin = parseFloat(form.amount_min), amax = parseFloat(form.amount_max);
    if (isNaN(amin) || isNaN(amax) || amin < 1 || amax > 100000 || amin > amax) return toast.error("Amount must be between ₹1 and ₹1,00,000");
    const dl = limitVal(form.daily_limit), ll = limitVal(form.lifetime_limit);
    if (badLimit(dl) || badLimit(ll)) return toast.error("Limits must be a positive amount or blank");
    setBusy(true);
    try {
      await api.post("/businesses", {
        name: form.name, upi_id: form.upi_id.trim(), qr_image: form.qr_image,
        amount_min: amin, amount_max: amax, merchant_ids: form.merchant_ids,
        google_account_email: form.google_account_email.trim(), google_business_name: form.google_business_name.trim(),
        google_business_phone: form.google_business_phone.trim(),
        payment_provider: form.payment_provider, daily_limit: dl, lifetime_limit: ll,
        bank_account_number: form.bank_account_number.trim(), bank_ifsc: form.bank_ifsc.trim().toUpperCase(), bank_name: form.bank_name.trim(),
      });
      toast.success("QR added");
      setOpen(false); setForm(empty); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setStatus = async (b, status, password) => {
    try { await api.post(`/businesses/${b.id}/status`, { status, ...(password ? { password } : {}) }); toast.success(`QR set to ${QR_STATUS[status][0]}`); setCloseTarget(null); setClosePw(""); setCloseErr(""); reload(); }
    catch (e) { const m = apiError(e); toast.error(m); if (status === "closed") setCloseErr(m); }
  };
  const byStatus = businesses.filter((b) => statusFilter === "all" || (b.qr_status || (b.is_active ? "active" : "inactive")) === statusFilter);
  const QR_COLS = ["QR ID", "QR Name", "Business Name / Account Name", "UPI ID", "Amount Range", "Branch", "Status", "Today / Daily Limit", "Lifetime / Limit", "Received", "Subs", "Matched", "Pending"];
  const qrRow = (b) => [b.business_id, b.name, b.google_business_name || "", b.upi_id, `${b.amount_min} - ${b.amount_max}`, b.branch_name || "",
    QR_STATUS[b.qr_status || (b.is_active ? "active" : "inactive")][0],
    `${b.usage?.today ?? 0} / ${b.daily_limit ?? "No limit"}`, `${b.usage?.lifetime ?? 0} / ${b.lifetime_limit ?? "No limit"}`,
    b.stats?.received_total ?? 0, b.stats?.submissions ?? 0, b.stats?.matched ?? 0, b.stats?.pending ?? 0];
  const [qrQuery, setQrQuery, shown] = useTableSearch(byStatus, qrRow);

  return (
    <div className={`${card} p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900">QR Management</h2>
          <p className="text-sm text-slate-500">{canWrite ? "Add QR images with a UPI ID, allowed amount range and assigned merchant. Any number of QRs can be Active at once." : "View-only: monitor all QR codes across branches. Editing is available to Branches only."}</p>
        </div>
        {canWrite && (
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-business-button" className={primaryBtn}><Plus className="w-4 h-4 mr-1" />Add QR</Button>
          </DialogTrigger>
          <DialogContent className={dialogCls}>
            <DialogHeader><DialogTitle>Add QR</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-500">QR Name</Label><Input data-testid="business-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
              <div><Label className="text-xs text-slate-500">UPI ID <span className="text-rose-500">*</span> <span className="text-slate-400">(auto-filled from QR image; correct it if needed)</span></Label><Input data-testid="business-upi_id-input" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} className={inputCls} /></div>
              <div><Label className="text-xs text-slate-500">Email <span className="text-rose-500">*</span></Label><Input data-testid="business-google-email-input" type="email" placeholder="business@gmail.com" value={form.google_account_email} onChange={(e) => setForm({ ...form, google_account_email: e.target.value })} className={inputCls} /></div>
              <div><Label className="text-xs text-slate-500">Business Name / Account Name <span className="text-rose-500">*</span></Label><Input data-testid="business-google-name-input" value={form.google_business_name} onChange={(e) => setForm({ ...form, google_business_name: e.target.value })} className={inputCls} /></div>
              <div><Label className="text-xs text-slate-500">Bank Name <span className="text-rose-500">*</span></Label><Input data-testid="business-bank-name-input" placeholder="HDFC Bank" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className={inputCls} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Account Number <span className="text-rose-500">*</span></Label><Input data-testid="business-account-number-input" inputMode="numeric" value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} className={`${inputCls} font-mono`} /></div>
                <div><Label className="text-xs text-slate-500">IFSC Code <span className="text-rose-500">*</span></Label><Input data-testid="business-ifsc-input" placeholder="HDFC0001234" maxLength={11} value={form.bank_ifsc} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} /></div>
              </div>
              <div><Label className="text-xs text-slate-500">Mobile Number <span className="text-rose-500">*</span></Label><Input data-testid="business-google-phone-input" placeholder="+91 96924 81524" value={form.google_business_phone} onChange={(e) => setForm({ ...form, google_business_phone: e.target.value })} className={inputCls} /></div>
              <div><Label className="text-xs text-slate-500">Payment App <span className="text-rose-500">*</span></Label>
                <Select value={form.payment_provider} onValueChange={(v) => setForm({ ...form, payment_provider: v })}>
                  <SelectTrigger data-testid="business-payment-provider-select" className="mt-1 bg-white border-slate-300 text-slate-900"><SelectValue placeholder="Select payment app" /></SelectTrigger>
                  <SelectContent className={dialogCls}>
                    <SelectItem value="gpay" data-testid="payment-provider-gpay">Google Pay</SelectItem>
                    <SelectItem value="bharatpe" data-testid="payment-provider-bharatpe">Bharat Pay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Daily Limit (₹) <span className="text-slate-400">optional</span></Label><Input data-testid="business-daily-limit-input" type="number" min="1" placeholder="No limit" value={form.daily_limit} onChange={(e) => setForm({ ...form, daily_limit: e.target.value })} className={inputCls} /></div>
                <div><Label className="text-xs text-slate-500">Lifetime Limit (₹) <span className="text-slate-400">optional</span></Label><Input data-testid="business-lifetime-limit-input" type="number" min="1" placeholder="No limit" value={form.lifetime_limit} onChange={(e) => setForm({ ...form, lifetime_limit: e.target.value })} className={inputCls} /></div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">QR Image <span className="text-rose-500">*</span></Label>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="qr-image-input" />
                <button type="button" onClick={() => fileRef.current?.click()} className="mt-1 w-full border-2 border-dashed border-slate-300 rounded-lg p-4 flex flex-col items-center gap-2 hover:border-teal-400">
                  {form.qr_image ? <img src={fileUrl(form.qr_image)} alt="qr" className="w-24 h-24 object-contain rounded bg-white p-1 border border-slate-200" /> : (uploading ? <Loader2 className="w-5 h-5 animate-spin text-teal-600" /> : <UploadCloud className="w-5 h-5 text-teal-600" />)}
                  <span className="text-xs text-slate-500">{form.qr_image ? "Change image" : "Upload QR image"}</span>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="hidden"><Label className="text-xs text-slate-500">Amount Min (₹)</Label><Input data-testid="business-amount-min-input" value={form.amount_min} onChange={(e) => setForm({ ...form, amount_min: e.target.value })} className={inputCls} /></div>
                <div className="hidden"><Label className="text-xs text-slate-500">Amount Max (₹)</Label><Input data-testid="business-amount-max-input" value={form.amount_max} onChange={(e) => setForm({ ...form, amount_max: e.target.value })} className={inputCls} /></div>
              </div>
              {isSuper && (
              <div>
                <Label className="text-xs text-slate-500">Assign Merchants (a QR can serve many)</Label>
                <div data-testid="business-merchant-select" className="mt-1 space-y-1.5 max-h-32 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
                  {merchants.length === 0 && <p className="text-xs text-slate-400">No merchants yet.</p>}
                  {merchants.map((v) => (
                    <label key={v.id} data-testid={`merchant-opt-${v.merchant_code}`} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" className="accent-teal-600" checked={form.merchant_ids.includes(v.id)} onChange={(e) => setForm({ ...form, merchant_ids: e.target.checked ? [...form.merchant_ids, v.id] : form.merchant_ids.filter((x) => x !== v.id) })} />
                      <span>{v.name || v.merchant_code} <span className="text-slate-400 font-mono text-xs">{v.merchant_code}</span></span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">Each merchant only ever sees their own customers' transactions.</p>
              </div>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="save-business-button" onClick={create} disabled={busy} className={primaryBtn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save QR"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="qr-status-filter" className="h-9 w-48 bg-white border-slate-300 text-slate-900"><SelectValue /></SelectTrigger>
          <SelectContent className={dialogCls}>
            <SelectItem value="active" data-testid="qr-status-filter-active">Active</SelectItem>
            <SelectItem value="inactive" data-testid="qr-status-filter-inactive">Inactive</SelectItem>
            <SelectItem value="closed" data-testid="qr-status-filter-closed">Permanently Closed</SelectItem>
            <SelectItem value="all" data-testid="qr-status-filter-all">All</SelectItem>
          </SelectContent>
        </Select>
        <TableTools id="qr" query={qrQuery} onQuery={setQrQuery} cols={QR_COLS} rows={shown.map(qrRow)} title="ABCD Pay — QR list" filename="abcdpay-qrs" />
      </div>
      <div className="overflow-x-auto mt-3">
        <table className="w-full min-w-[1280px]">
          <thead><tr className="border-b border-slate-200">
            <Th></Th><Th>QR</Th><Th>QR Name</Th><Th>Business Name / Account Name</Th><Th>UPI ID</Th><Th>Amount Range</Th><Th>Branch</Th><Th>Status</Th><Th>Today / Daily</Th><Th>Lifetime</Th><Th>Received</Th><Th>Subs</Th><Th>Matched</Th><Th>Pending</Th><Th>Actions</Th>
          </tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={15} className="py-8 text-center text-sm text-slate-400" data-testid="qr-empty">No QRs match this filter.</td></tr>}
            {shown.map((b) => { const st = b.qr_status || (b.is_active ? "active" : "inactive"); return (
              <ExpandableRow key={b.id} rowId={b.business_id} testPrefix="qr" rowTestId={`business-row-${b.business_id}`} items={qrDetails(b, st)} cells={[
                <Td key="img"><img src={fileUrl(b.qr_image)} alt="qr" className="w-10 h-10 rounded bg-white p-0.5 border border-slate-200" /></Td>,
                <Td key="n"><div className="font-semibold text-slate-800" data-testid={`qr-name-${b.business_id}`}>{b.name}</div><div className="text-xs text-slate-400 font-mono">{b.business_id}</div></Td>,
                <Td key="bn"><span className="text-sm text-slate-700" data-testid={`qr-business-name-${b.business_id}`}>{b.google_business_name || "-"}</span></Td>,
                <Td key="u" mono>{b.upi_id}</Td>,
                <Td key="r"><span className="text-xs">{money(b.amount_min)} – {money(b.amount_max)}</span></Td>,
                <Td key="br">{b.branch_name ? <span className="text-xs text-slate-600">{b.branch_name}</span> : <span className="text-xs text-slate-400">—</span>}</Td>,
                <Td key="st"><span data-testid={`qr-status-${b.business_id}`} className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${QR_STATUS[st][1]}`}>{QR_STATUS[st][0]}</span></Td>,
                <Td key="du"><UsageBar used={b.usage?.today || 0} limit={b.daily_limit} testid={`qr-daily-usage-${b.business_id}`} /></Td>,
                <Td key="lu"><UsageBar used={b.usage?.lifetime || 0} limit={b.lifetime_limit} testid={`qr-lifetime-usage-${b.business_id}`} /></Td>,
                <Td key="rc"><div className="text-slate-800">{money(b.stats?.received_total)}</div><div className="text-xs text-slate-400">{b.stats?.received_count} txns</div></Td>,
                <Td key="sb">{b.stats?.submissions}</Td>,
                <Td key="m"><span className="text-teal-600 font-semibold">{b.stats?.matched}</span></Td>,
                <Td key="p"><span className="text-amber-600 font-semibold">{b.stats?.pending}</span></Td>,
                <Td key="a">
                  {canWrite ? (st === "closed" ? <span className="text-xs text-slate-400">Closed</span> : (
                  <div className="flex gap-2">
                    <Button data-testid={`edit-business-${b.business_id}`} onClick={() => setEditBiz(b)} size="sm" variant="outline" className={`${outlineBtn} h-8`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button data-testid={`toggle-business-${b.business_id}`} onClick={() => setStatus(b, st === "active" ? "inactive" : "active")} size="sm" variant="outline" className={`${outlineBtn} h-8`}>
                      <Power className="w-3.5 h-3.5 mr-1" />{st === "active" ? "Deactivate" : "Activate"}
                    </Button>
                    <Button data-testid={`close-business-${b.business_id}`} onClick={() => { setCloseTarget(b); setClosePw(""); setCloseErr(""); }} size="sm" variant="outline" className="border-rose-200 bg-white text-rose-600 hover:bg-rose-50 h-8">Close</Button>
                  </div>
                  )) : (st === "closed"
                    ? <Button data-testid={`reopen-business-${b.business_id}`} onClick={() => setStatus(b, "inactive")} size="sm" variant="outline" className={`${outlineBtn} h-8`}>Reopen</Button>
                    : <span className="text-xs text-slate-400">View only</span>)}
                </Td>,
              ]} />
            ); })}
          </tbody>
        </table>
      </div>
      <Dialog open={!!closeTarget} onOpenChange={(o) => !o && setCloseTarget(null)}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle>Permanently close QR?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Close <span className="font-semibold">{closeTarget?.name}</span> permanently? It stops receiving payments right away, and you can't reactivate it. Only a Super Admin can reopen it.</p>
          <div>
            <Label className="text-xs text-slate-500">Enter your login password to confirm <span className="text-rose-500">*</span></Label>
            <Input data-testid="close-password-input" type="password" autoComplete="current-password" value={closePw} onChange={(e) => { setClosePw(e.target.value); setCloseErr(""); }} className={inputCls} />
            {closeErr && <p data-testid="close-password-error" className="text-xs text-rose-600 mt-1">{closeErr}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" className={outlineBtn} onClick={() => setCloseTarget(null)}>Cancel</Button>
            <Button data-testid="confirm-close-business-button" disabled={!closePw} onClick={() => setStatus(closeTarget, "closed", closePw)} className="bg-rose-600 hover:bg-rose-700 text-white">Close permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {editBiz && <EditQrDialog biz={editBiz} merchants={merchants} onClose={() => setEditBiz(null)} reload={reload} isSuper={isSuper} />}
    </div>
  );
}

/* ---------------- Received Payments ---------------- */
function ReceivedTab({ businesses, received, reload, isSuper }) {
  const [sub, setSub] = useState("single");
  const [bizId, setBizId] = useState("");
  const [txn, setTxn] = useState("");
  const [amount, setAmount] = useState("");
  const [bulk, setBulk] = useState("");
  const [busy, setBusy] = useState(false);

  const addSingle = async () => {
    if (!bizId || !txn || !amount) return toast.error("All fields required");
    setBusy(true);
    try {
      await api.post("/received-payments", { business_id: bizId, transaction_id: txn.trim(), amount: parseFloat(amount) });
      toast.success("Entry added"); setTxn(""); setAmount(""); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const addBulk = async () => {
    if (!bizId) return toast.error("Select a QR");
    const entries = bulk.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [t, a] = l.split(/[,\t]/).map((x) => x.trim());
      return { transaction_id: t, amount: parseFloat(a) };
    }).filter((e) => e.transaction_id && !isNaN(e.amount));
    if (!entries.length) return toast.error("Add lines like: TXN123, 500");
    setBusy(true);
    try {
      const { data } = await api.post("/received-payments/bulk", { business_id: bizId, entries });
      toast.success(`${data.created} entries added`); setBulk(""); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const uploadCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!bizId) { toast.error("Select a QR first"); return; }
    const fd = new FormData(); fd.append("file", f); fd.append("business_id", bizId);
    try {
      const { data } = await api.post("/received-payments/csv", fd);
      toast.success(`${data.created} rows imported`); reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  const bizSelect = (
    <Select value={bizId} onValueChange={setBizId}>
      <SelectTrigger data-testid="received-business-select" className="bg-white border-slate-300 text-slate-900 w-full sm:w-64"><SelectValue placeholder="Select QR" /></SelectTrigger>
      <SelectContent className={dialogCls}>
        {businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const recRow = (r) => [r.business_name || "", r.transaction_id || "", r.amount ?? "", r.date || "", r.submission?.payer_upi || "", r.submission?.order_id || "", r.submission?.user_id || "", r.submission?.utr || ""];
  const [recQuery, setRecQuery, recShown] = useTableSearch(received, recRow);

  return (
    <div className={`${card} p-5`}>
      <h2 className="text-lg font-bold tracking-tight text-slate-900">Branch Received Payments</h2>
      <p className="text-sm text-slate-500">{isSuper ? "View-only: monitor recorded bank credits across all branches. Recording entries is available to Branches only." : "Record UPI payments received against a QR."}</p>

      {!isSuper && (
      <Tabs value={sub} onValueChange={setSub} className="mt-4">
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger value="single" data-testid="received-subtab-single">Single</TabsTrigger>
          <TabsTrigger value="multiple" data-testid="received-subtab-multiple">Multiple</TabsTrigger>
          <TabsTrigger value="csv" data-testid="received-subtab-csv">CSV Upload</TabsTrigger>
        </TabsList>

        <TabsContent value="single" className="mt-4">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            {bizSelect}
            <Input data-testid="received-txn-input" placeholder="Transaction ID" value={txn} onChange={(e) => setTxn(e.target.value)} className="bg-white border-slate-300 text-slate-900 font-mono" />
            <Input data-testid="received-amount-input" placeholder="Amount ₹" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-white border-slate-300 text-slate-900 w-full sm:w-32" />
            <Button data-testid="add-entry-button" onClick={addSingle} disabled={busy} className={`${primaryBtn} shrink-0`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Entry"}</Button>
          </div>
        </TabsContent>

        <TabsContent value="multiple" className="mt-4">
          <div className="space-y-3">
            {bizSelect}
            <textarea data-testid="received-bulk-textarea" value={bulk} onChange={(e) => setBulk(e.target.value)} rows={5} placeholder={"One per line:\nTXN12345, 500\nTXN67890, 1200"} className="w-full rounded-lg bg-white border border-slate-300 text-slate-900 p-3 text-sm font-mono" />
            <Button data-testid="add-bulk-button" onClick={addBulk} disabled={busy} className={primaryBtn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Entries"}</Button>
          </div>
        </TabsContent>

        <TabsContent value="csv" className="mt-4">
          <div className="space-y-3">
            {bizSelect}
            <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-teal-400 cursor-pointer">
              <input data-testid="received-csv-input" type="file" accept=".csv" className="hidden" onChange={uploadCsv} />
              <span className="text-sm text-slate-500">Drop a CSV with <span className="font-mono text-slate-700">transaction_id, amount</span> columns</span>
            </label>
          </div>
        </TabsContent>
      </Tabs>
      )}

      <div className="mt-5 flex justify-end"><TableTools id="received" query={recQuery} onQuery={setRecQuery} cols={["QR", "Transaction ID", "Amount", "Date", "Payer UPI", "Order ID", "User ID", "UTR"]} rows={recShown.map(recRow)} title="ABCD Pay — Received Payments" filename="abcdpay-received" /></div>
      <div className="overflow-x-auto mt-3">
        <table className="w-full min-w-[600px]">
          <thead><tr className="border-b border-slate-200"><Th></Th><Th>QR</Th><Th>Transaction ID</Th><Th>Amount</Th><Th>Date</Th></tr></thead>
          <tbody>
            {recShown.map((r) => <ReceivedRow key={r.id} r={r} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReceivedRow({ r }) {
  const s = r.submission || {};
  const items = [
    ["Exact time", exactTime(r.date)], ["Recorded at", exactTime(r.received_at)], ["Bank status", r.status],
    ["Source", r.source === "api" ? "Extension" : `Manual${r.entered_by ? " · " + r.entered_by : ""}`],
    ...payerItems(s),
    ["Claim status", s.status ? <StatusPill status={s.status} /> : "No customer claim yet"],
    ["Claim submitted", s.submitted_at && exactTime(s.submitted_at)], ["Matched at", s.matched_at && exactTime(s.matched_at)],
  ];
  return (
    <ExpandableRow rowId={r.id} testPrefix="received" items={items} cells={[
      <Td key="q">{r.business_name}</Td>, <Td key="t" mono>{r.transaction_id}</Td>, <Td key="a">{money(r.amount)}</Td>, <Td key="d">{timeAgo(r.date)}</Td>,
    ]} />
  );
}

const pct = (v) => (v != null ? `${v}%` : null);
export const payinItems = (s, full) => [
  ["Request amount", s.request_amount != null ? money(s.request_amount) : null],
  ["Transaction amount", s.transaction_amount != null ? money(s.transaction_amount) : null],
  ["Request time", exactTime(s.requested_at || (s.status === "Requested" ? s.submitted_at : null))],
  ["Submitted time", s.transaction_id && s.submitted_at ? exactTime(s.submitted_at) : null],
  ["Verified time", s.matched_at && exactTime(s.matched_at)],
  ["Status", s.status ? <StatusPill status={s.status} /> : null],
  ["Source", s.source === "api" ? "API" : "Manual"],
  ["Match insight", s.match_insight],
  ...payerItems(s).filter(([k]) => full || k !== "Merchant"),
  ...(full ? [
    ["Commission", s.commission_amount != null ? money(s.commission_amount) : null], ["Commission %", pct(s.commission_rate)],
    ["IP address", s.ip_address], ["Device ID", s.device_id], ["User agent", s.user_agent],
    ["Fraud flag", s.fraud_flag == null ? null : (s.fraud_flag ? "Flagged" : "No")], ["Fraud reason", s.fraud_reason],
  ] : []),
];

/* ---------------- Customer Payments (list) ---------------- */
function CustomerTab({ rows, branchOf }) {
  const defFrom = isoDate(new Date(Date.now() - 6 * 86400000));
  const defTo = isoDate(new Date());
  const [from, setFrom] = useState(defFrom);
  const [to, setTo] = useState(defTo);

  const dateFiltered = rows.filter((s) => {
    const d = (s.submitted_at || "").slice(0, 10);
    return d && d >= from && d <= to;
  });

  const txnAmount = (s) => (s.transaction_amount != null ? s.transaction_amount : (s.status !== "Requested" && s.amount != null ? s.amount : ""));
  const sourceLabel = (s) => (s.source === "api" ? `API · ${s.merchant_code || ""}`.trim() : "Manual");
  const COLS = ["QR", "Transaction ID", "Req. Amount", "Txn Amount", "Commission", "Status", "Source", "Match Insight", "Submitted", "Matched"];
  const toRow = (s) => [
    s.business_name || "", s.transaction_id || "",
    s.request_amount != null ? s.request_amount : "", txnAmount(s),
    s.commission_amount != null ? s.commission_amount : "",
    s.status || "", sourceLabel(s), s.match_insight || "",
    s.submitted_at || "", s.matched_at || "",
  ];
  const [payinQuery, setPayinQuery, filtered] = useTableSearch(dateFiltered, toRow);

  return (
    <div className={`${card} p-5`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900">PayIn</h2>
          <p className="text-sm text-slate-500">Submissions from customers and their live match status.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} testId="payin-range" />
          <TableTools id="payin" query={payinQuery} onQuery={setPayinQuery} cols={COLS} rows={filtered.map(toRow)} title={`ABCD Pay PayIn — ${from} to ${to}`} filename={`abcdpay-payin_${from}_to_${to}`} />
        </div>
      </div>
      <div className="overflow-x-auto mt-4">
        <table className="w-full min-w-[980px]">
          <thead><tr className="border-b border-slate-200">
            <Th></Th><Th>QR</Th><Th>Transaction ID</Th><Th>Screenshot</Th><Th>Req. Amount</Th><Th>Txn Amount</Th><Th>Commission</Th><Th>Status</Th><Th>Source</Th><Th>Match Insight</Th><Th>Submitted</Th><Th>Matched</Th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-sm text-slate-400">No PayIn records in this range.</td></tr>}
            {filtered.map((s) => (
              <ExpandableRow key={s.id} rowId={s.id} testPrefix="payin" rowTestId={`customer-row-${s.transaction_id || s.id}`} items={[
                ["Screenshot", s.screenshot_url ? <div className="flex items-center gap-2"><ScreenshotThumb s={s} /><PayinShare s={s} branchName={branchOf(s)} /></div> : null],
                ...payinItems(s, true)]} cells={[
                <Td key="q">{s.business_name}</Td>,
                <Td key="t" mono>{s.transaction_id || <span className="text-slate-400">—</span>}</Td>,
                <Td key="ss"><div className="flex items-center gap-2"><ScreenshotThumb s={s} />{s.screenshot_url && <PayinShare s={s} branchName={branchOf(s)} />}</div></Td>,
                <Td key="r">{s.request_amount != null ? money(s.request_amount) : "—"}</Td>,
                <Td key="x">{s.transaction_amount != null ? money(s.transaction_amount) : (s.status !== "Requested" && s.amount != null ? money(s.amount) : "—")}</Td>,
                <Td key="c" data-testid={`payin-commission-${s.transaction_id || s.id}`}>{s.commission_amount != null ? money(s.commission_amount) : "—"}</Td>,
                <Td key="s"><StatusPill status={s.status} /></Td>,
                <Td key="o">{s.source === "api"
                  ? <span className="text-xs px-2 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">API · {s.merchant_code}</span>
                  : <span className="text-xs text-slate-500">Manual</span>}</Td>,
                <Td key="i"><span className="text-xs text-slate-500">{s.match_insight}</span></Td>,
                <Td key="sa"><RelativeTime iso={s.submitted_at} /></Td>,
                <Td key="m">{s.matched_at ? <RelativeTime iso={s.matched_at} /> : "—"}</Td>,
              ]} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Payout (Branch request; Super Admin view-only) ---------------- */
function PayoutTab({ isSuper }) {
  const canWrite = !isSuper;
  const today = isoDate(new Date());
  const emptyForm = { date: today, amount: "", screenshot: "", transaction_id: "", customer_id: "", merchant_id: "", upi_id: "", qr_id: "", bank_name: "", bank_account_number: "", bank_account_holder_name: "", bank_ifsc_code: "" };
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [branches, setBranches] = useState([]);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignBranch, setAssignBranch] = useState("");
  const [payTarget, setPayTarget] = useState(null);
  const [payTx, setPayTx] = useState("");
  const [paySshot, setPaySshot] = useState("");
  const [payUploading, setPayUploading] = useState(false);
  const payFileRef = useRef();
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [from, setFrom] = useState(isoDate(new Date(Date.now() - 6 * 86400000)));
  const [to, setTo] = useState(today);

  const load = () => api.get("/payouts").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []);
  useEffect(() => { if (isSuper) api.get("/branches").then((r) => setBranches(r.data.filter((b) => b.role === "branch" && b.is_active))).catch(() => {}); }, [isSuper]);

  const doAssign = async () => {
    if (!assignBranch) return toast.error("Select a branch");
    try { await api.post(`/payouts/${assignTarget.id}/assign`, { branch_id: assignBranch }); toast.success("Assigned to branch"); setAssignTarget(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const onPayImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setPayUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/branch/upload-image", fd); setPaySshot(data.url); toast.success("Screenshot uploaded"); }
    catch (err) { toast.error(apiError(err)); } finally { setPayUploading(false); }
  };
  const doPay = async () => {
    if (!payTx) return toast.error("Enter the TX ID");
    try { await api.post(`/payouts/${payTarget.id}/pay`, { transaction_id: payTx, screenshot: paySshot }); toast.success("Payment submitted for approval"); setPayTarget(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/branch/upload-image", fd);
      setForm((p) => ({ ...p, screenshot: data.url }));
      toast.success("Screenshot uploaded");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!form.date) return toast.error("Payout date is required");
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/payouts", { ...form, amount: amt });
      toast.success("Payout request submitted");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const dateFiltered = rows.filter((p) => { const d = (p.date || "").slice(0, 10); return d && d >= from && d <= to; });

  const COLS = ["Date", "Amount", "Commission", "TX ID", "CX ID", "Merchant", "UPI ID", "QR", "Bank Name", "Account No", "Holder", "IFSC", "Status", ...(isSuper ? ["Branch"] : []), "Submitted"];
  const toRow = (p) => [
    p.date || "", p.amount != null ? p.amount : "", p.commission_amount != null ? p.commission_amount : "",
    p.transaction_id || "", p.customer_id || "",
    p.merchant_code || p.merchant_id || "", p.upi_id || "", p.qr_name || p.qr_id || "",
    p.bank_name || "", p.bank_account_number || "", p.bank_account_holder_name || "", p.bank_ifsc_code || "",
    p.status || "", ...(isSuper ? [p.branch_name || ""] : []), p.submitted_at || "",
  ];
  const [payoutQuery, setPayoutQuery, filtered] = useTableSearch(dateFiltered, toRow);

  const F = ({ k, label, type = "text" }) => (
    <div><Label className="text-xs text-slate-500">{label}</Label><Input data-testid={`payout-${k}-input`} type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inputCls} /></div>
  );

  return (
    <div className={`${card} p-5`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900">Payout</h2>
          <p className="text-sm text-slate-500">{canWrite ? "Submit payout requests and track their status." : "View-only: payout requests across all branches."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} testId="payout-range" />
          <TableTools id="payout" query={payoutQuery} onQuery={setPayoutQuery} cols={COLS} rows={filtered.map(toRow)} title={`ABCD Pay Payouts — ${from} to ${to}`} filename={`abcdpay-payouts_${from}_to_${to}`} />
          {canWrite && <Button data-testid="request-payout-button" onClick={() => { setForm(emptyForm); setOpen(true); }} className={primaryBtn}><Plus className="w-4 h-4 mr-1" />Request Payout</Button>}
        </div>
      </div>

      <div className="overflow-x-auto mt-4">
        <table className="w-full min-w-[1200px]">
          <thead><tr className="border-b border-slate-200">
            <Th>Action</Th><Th>Date</Th><Th>Amount</Th><Th>Commission</Th><Th>TX ID</Th><Th>CX ID</Th><Th>Merchant</Th><Th>UPI</Th><Th>Bank Details</Th><Th>Status</Th>{isSuper && <Th>Branch</Th>}<Th>Screenshot</Th><Th>Submitted</Th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={isSuper ? 13 : 12} className="py-8 text-center text-sm text-slate-400">No payout requests in this range.</td></tr>}
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-slate-100" data-testid={`payout-row-${p.transaction_id || p.id}`}>
                <Td>{isSuper
                  ? (p.status === "Requested"
                    ? <button data-testid={`payout-pass-btn-${p.id}`} onClick={() => { setAssignTarget(p); setAssignBranch(""); }} className="text-xs px-2.5 py-1 rounded bg-teal-600 text-white hover:bg-teal-700">Pass</button>
                    : <span className="text-slate-400 text-xs">—</span>)
                  : (p.status === "Assigned"
                    ? <button data-testid={`payout-pay-btn-${p.id}`} onClick={() => { setPayTarget(p); setPayTx(""); setPaySshot(""); }} className="text-xs px-2.5 py-1 rounded bg-teal-600 text-white hover:bg-teal-700">Pay</button>
                    : <span className="text-slate-400 text-xs">—</span>)}</Td>
                <Td>{p.date}</Td>
                <Td>{p.amount != null ? money(p.amount) : "—"}</Td>
                <Td data-testid={`payout-commission-${p.transaction_id || p.id}`}>{p.commission_amount != null ? money(p.commission_amount) : "—"}</Td>
                <Td mono>{p.transaction_id || <span className="text-slate-400">—</span>}</Td>
                <Td mono>{p.customer_id || <span className="text-slate-400">—</span>}</Td>
                <Td>{p.merchant_code || p.merchant_id || <span className="text-slate-400">—</span>}</Td>
                <Td><button data-testid={`payout-upi-btn-${p.transaction_id || p.id}`} onClick={() => setDetail({
                  title: "UPI Details",
                  rows: [["UPI ID", p.upi_id || "—"], ["QR Reference", p.qr_name || p.qr_id || "—"]],
                  image: p.qr_image,
                })} className="text-xs px-2.5 py-1 rounded border border-slate-200 text-teal-700 hover:bg-teal-50">View UPI</button></Td>
                <Td><button data-testid={`payout-bank-btn-${p.transaction_id || p.id}`} onClick={() => setDetail({
                  title: "Bank Details",
                  rows: [["Bank Name", p.bank_name || "—"], ["Account Number", p.bank_account_number || "—"], ["Account Holder", p.bank_account_holder_name || "—"], ["IFSC Code", p.bank_ifsc_code || "—"]],
                })} className="text-xs px-2.5 py-1 rounded border border-slate-200 text-teal-700 hover:bg-teal-50">View Bank</button></Td>
                <Td><StatusPill status={p.status} /></Td>
                {isSuper && <Td>{p.branch_name || "—"}</Td>}
                <Td>{p.screenshot ? <a href={fileUrl(p.screenshot)} target="_blank" rel="noreferrer" className="text-teal-600 underline text-xs">View</a> : <span className="text-slate-400">—</span>}</Td>
                <Td><RelativeTime iso={p.submitted_at} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(emptyForm); }}>
        <DialogContent className={`${dialogCls} max-w-2xl`}>
          <DialogHeader><DialogTitle>Request Payout</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
            <F k="date" label="Date" type="date" />
            <F k="amount" label="Amount (₹)" type="number" />
            <F k="customer_id" label="Customer ID (CX ID)" />
            <F k="merchant_id" label="Merchant ID" />
            <F k="upi_id" label="UPI ID" />
            <F k="qr_id" label="QR (from customer)" />
            <F k="bank_name" label="Bank Name" />
            <F k="bank_account_number" label="Bank Account Number" />
            <F k="bank_account_holder_name" label="Account Holder Name" />
            <F k="bank_ifsc_code" label="IFSC Code" />
            <div>
              <Label className="text-xs text-slate-500">Screenshot</Label>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="payout-screenshot-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="mt-1 w-full border-2 border-dashed border-slate-300 rounded-lg p-3 flex flex-col items-center gap-1.5 hover:border-teal-400">
                {form.screenshot ? <img src={fileUrl(form.screenshot)} alt="proof" className="w-16 h-16 object-contain rounded bg-white p-1 border border-slate-200" /> : (uploading ? <Loader2 className="w-5 h-5 animate-spin text-teal-600" /> : <UploadCloud className="w-5 h-5 text-teal-600" />)}
                <span className="text-xs text-slate-500">{form.screenshot ? "Change" : "Upload"}</span>
              </button>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-payout-button" onClick={submit} disabled={busy} className={primaryBtn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Request"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className={dialogCls} data-testid="payout-detail-modal">
          <DialogHeader><DialogTitle>{detail?.title}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(detail?.rows || []).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 text-sm border-b border-slate-100 pb-1.5">
                <span className="text-slate-500">{k}</span>
                <span className="font-mono text-slate-800 text-right break-all">{v}</span>
              </div>
            ))}
            {detail?.image && (
              <div className="pt-2 flex flex-col items-center gap-1">
                <img src={fileUrl(detail.image)} alt="QR" className="w-40 h-40 object-contain rounded bg-white p-1 border border-slate-200" data-testid="payout-detail-qr" />
                <span className="text-xs text-slate-400">Customer QR</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
        <DialogContent className={dialogCls} data-testid="payout-assign-modal">
          <DialogHeader><DialogTitle>Assign Payout to Branch</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Branch (active only)</Label>
            <select data-testid="payout-assign-branch-select" value={assignBranch} onChange={(e) => setAssignBranch(e.target.value)} className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900">
              <option value="">Select a branch…</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name || b.email}</option>)}
            </select>
          </div>
          <DialogFooter><Button data-testid="confirm-assign-button" onClick={doAssign} className={primaryBtn}>Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payTarget} onOpenChange={(o) => { if (!o) { setPayTarget(null); setPayTx(""); setPaySshot(""); } }}>
        <DialogContent className={dialogCls} data-testid="payout-pay-modal">
          <DialogHeader><DialogTitle>Submit Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Transaction ID (TX ID) <span className="text-rose-500">*</span></Label>
              <Input data-testid="pay-txid-input" value={payTx} onChange={(e) => setPayTx(e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Payment Screenshot</Label>
              <input ref={payFileRef} type="file" accept="image/*" className="hidden" onChange={onPayImage} data-testid="pay-screenshot-input" />
              <button type="button" onClick={() => payFileRef.current?.click()} className="mt-1 w-full border-2 border-dashed border-slate-300 rounded-lg p-3 flex flex-col items-center gap-1.5 hover:border-teal-400">
                {paySshot ? <img src={fileUrl(paySshot)} alt="proof" className="w-16 h-16 object-contain rounded bg-white p-1 border border-slate-200" /> : (payUploading ? <Loader2 className="w-5 h-5 animate-spin text-teal-600" /> : <UploadCloud className="w-5 h-5 text-teal-600" />)}
                <span className="text-xs text-slate-500">{paySshot ? "Change" : "Upload"}</span>
              </button>
            </div>
          </div>
          <DialogFooter><Button data-testid="confirm-pay-button" onClick={doPay} className={primaryBtn}>Submit Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Manage Branches (Super Admin only) ---------------- */
function BranchesTab({ meId }) {
  const [branches, setBranches] = useState([]);
  const [dlg, setDlg] = useState(null); // null | {mode:"create"} | {mode:"edit", branch}
  const [form, setForm] = useState({ name: "", email: "", password: "", payin_commission: "", payout_commission: "", max_limit: "1000000" });
  const [limits, setLimits] = useState({});
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => {
    api.get("/branches").then((r) => setBranches(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/super/branch-limits").then((r) => setLimits(Object.fromEntries((r.data || []).map((b) => [b.branch_id, b])))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ name: "", email: "", password: "", payin_commission: "", payout_commission: "", max_limit: "1000000" }); setDlg({ mode: "create" }); };
  const openEdit = (a) => { setForm({ name: a.name || "", email: a.email, password: "", payin_commission: String(a.payin_commission ?? ""), payout_commission: String(a.payout_commission ?? ""), max_limit: String(a.max_limit ?? 1000000) }); setDlg({ mode: "edit", branch: a }); };

  const save = async () => {
    if (dlg.mode === "create" && (!form.name || !form.email || !form.password)) return toast.error("Name, email and password are required");
    setBusy(true);
    try {
      if (dlg.mode === "create") {
        await api.post("/branches", { name: form.name, email: form.email, password: form.password, payin_commission: parseFloat(form.payin_commission) || 0, payout_commission: parseFloat(form.payout_commission) || 0, max_limit: parseFloat(form.max_limit) || 1000000 });
        toast.success("Branch created");
      } else {
        const body = { name: form.name, email: form.email, payin_commission: parseFloat(form.payin_commission) || 0, payout_commission: parseFloat(form.payout_commission) || 0, max_limit: parseFloat(form.max_limit) || 1000000 };
        if (form.password) body.password = form.password;
        await api.put(`/branches/${dlg.branch.id}`, body);
        toast.success("Branch updated");
      }
      setDlg(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggle = async (a) => {
    try { await api.post(`/branches/${a.id}/toggle`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const del = async () => {
    try { await api.delete(`/branches/${delTarget.id}`); toast.success("Branch deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const resetPw = async () => {
    if (!newPw) return toast.error("Enter a new password");
    setBusy(true);
    try {
      await api.post(`/branches/${pwTarget.id}/reset-password`, { new_password: newPw });
      toast.success("Password reset. Branch must change it on next login.");
      setPwTarget(null); setNewPw("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resetChatPin = async (a) => {
    if (!window.confirm(`Reset chat PIN for ${a.name || a.email}? Their chat will be deactivated until they set a new PIN.`)) return;
    try { await api.post(`/chat/pin/reset/${a.id}`); toast.success("Chat PIN reset. Chat deactivated for this Branch."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reset2fa = async (a) => {
    try { await api.post(`/branches/${a.id}/reset-2fa`); toast.success("2FA reset — branch can set it up again"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const brRow = (a) => { const L = limits[a.id]; return [a.name || "", a.email, a.role === "super_admin" ? "Super Admin" : "Branch",
    a.role === "super_admin" ? "" : `${Number(a.payin_commission ?? 0)}% / ${Number(a.payout_commission ?? 0)}%`,
    a.role === "super_admin" ? "" : `${L?.outstanding ?? 0} / ${L?.max_limit ?? a.max_limit ?? 1000000}`, a.is_active ? "Active" : "Disabled",
    a.qr_counts?.total ?? 0, a.qr_counts?.active ?? 0, a.qr_counts?.inactive ?? 0, a.qr_counts?.closed ?? 0, a.created_at || ""]; };
  const [brQuery, setBrQuery, brShown] = useTableSearch(branches, brRow);

  return (
    <div className={`${card} p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900">Manage Branches</h2>
          <p className="text-sm text-slate-500">Create, edit, disable or remove Branch accounts. Only Super Admins can access this screen.</p>
        </div>
        <Button data-testid="add-branch-button" onClick={openCreate} className={primaryBtn}><Plus className="w-4 h-4 mr-1" />Add Branch</Button>
      </div>
      <div className="mt-4 flex justify-end"><TableTools id="branches" query={brQuery} onQuery={setBrQuery} cols={["Name", "Email", "Role", "Commission (In/Out)", "Payin Limit", "Status", "Total QRs", "Active", "Inactive", "Closed", "Created"]} rows={brShown.map(brRow)} title="ABCD Pay — Branches" filename="abcdpay-branches" /></div>

      <div className="overflow-x-auto mt-3">
        <table className="w-full min-w-[1000px]">
          <thead><tr className="border-b border-slate-200"><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Commission (In/Out)</Th><Th>Payin Limit</Th><Th>Status</Th><Th>Total QRs</Th><Th>Active</Th><Th>Inactive</Th><Th>Closed</Th><Th>Created</Th><Th>Actions</Th></tr></thead>
          <tbody>
            {brShown.length === 0 && <tr><td colSpan={12} className="py-8 text-center text-sm text-slate-400">No branches yet.</td></tr>}
            {brShown.map((a) => {
              const isSuper = a.role === "super_admin";
              return (
                <tr key={a.id} className="border-b border-slate-100" data-testid={`branch-row-${a.email}`}>
                  <Td><span className="font-semibold text-slate-800">{a.name || "—"}</span></Td>
                  <Td>{a.email}</Td>
                  <Td>{isSuper
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">Super Admin</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Branch</span>}</Td>
                  <Td>{isSuper ? <span className="text-xs text-slate-400">—</span> : <span className="text-xs font-mono text-slate-600" data-testid={`branch-commission-${a.email}`}>{Number(a.payin_commission ?? 0)}% / {Number(a.payout_commission ?? 0)}%</span>}</Td>
                  <Td>{isSuper ? <span className="text-xs text-slate-400">—</span> : (() => {
                    const L = limits[a.id]; const lim = L?.max_limit ?? a.max_limit ?? 1000000; const out = L?.outstanding ?? 0;
                    const p = Math.min(100, (out / (lim || 1)) * 100);
                    const c = p > 90 ? "bg-rose-500" : p >= 70 ? "bg-amber-500" : "bg-emerald-500";
                    return <div className="w-36" data-testid={`branch-limit-bar-${a.email}`}><div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${c}`} style={{ width: p + "%" }} /></div><div className="text-[10px] text-slate-500 mt-1 font-mono">₹{Number(out).toLocaleString("en-IN")} / ₹{Number(lim).toLocaleString("en-IN")}</div></div>;
                  })()}</Td>
                  <Td>{a.is_active
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">Active</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200">Disabled</span>}</Td>
                  <Td><span className="font-semibold text-slate-800" data-testid={`branch-qr-total-${a.email}`}>{a.qr_counts?.total ?? 0}</span></Td>
                  <Td><span className="text-teal-600 font-semibold" data-testid={`branch-qr-active-${a.email}`}>{a.qr_counts?.active ?? 0}</span></Td>
                  <Td><span className="text-slate-500" data-testid={`branch-qr-inactive-${a.email}`}>{a.qr_counts?.inactive ?? 0}</span></Td>
                  <Td><span className="text-rose-600" data-testid={`branch-qr-closed-${a.email}`}>{a.qr_counts?.closed ?? 0}</span></Td>
                  <Td>{timeAgo(a.created_at)}</Td>
                  <Td>
                    {isSuper ? (
                      <span className="text-xs text-slate-400">—</span>
                    ) : (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => openEdit(a)} data-testid={`edit-branch-${a.email}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => { setPwTarget(a); setNewPw(""); }} data-testid={`reset-branch-pw-${a.email}`} title="Reset password"><KeyRound className="w-3.5 h-3.5" /></Button>
                        {a.totp_enabled && <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => reset2fa(a)} data-testid={`reset-branch-2fa-${a.email}`} title="Reset 2FA"><ShieldOff className="w-3.5 h-3.5" /></Button>}
                        {a.role === "branch" && <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => resetChatPin(a)} data-testid={`reset-branch-chat-pin-${a.email}`} title="Reset Chat PIN"><PinIcon className="w-3.5 h-3.5" /></Button>}
                        <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => toggle(a)} data-testid={`toggle-branch-${a.email}`}>{a.is_active ? "Disable" : "Enable"}</Button>
                        <Button size="sm" variant="outline" className="border-rose-200 bg-white text-rose-600 hover:bg-rose-50 h-8" onClick={() => setDelTarget(a)} data-testid={`delete-branch-${a.email}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle>{dlg?.mode === "edit" ? "Edit Branch" : "Add Branch"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-500">Name</Label><Input data-testid="branch-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-xs text-slate-500">Email</Label><Input data-testid="branch-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></div>
            {dlg?.mode === "create" && (
              <div>
                <Label className="text-xs text-slate-500">Password</Label>
                <Input data-testid="branch-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
                <p className="text-xs text-slate-400 mt-1">Min 8 chars incl. uppercase, lowercase, number and symbol.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500">PayIn Commission (%)</Label><Input data-testid="branch-payin-commission-input" type="number" min="0" step="0.01" value={form.payin_commission} onChange={(e) => setForm({ ...form, payin_commission: e.target.value })} className={inputCls} /></div>
              <div><Label className="text-xs text-slate-500">Payout Commission (%)</Label><Input data-testid="branch-payout-commission-input" type="number" min="0" step="0.01" value={form.payout_commission} onChange={(e) => setForm({ ...form, payout_commission: e.target.value })} className={inputCls} /></div>
            </div>
            <div><Label className="text-xs text-slate-500">Branch Payin Limit (₹)</Label><Input data-testid="branch-limit-input" type="number" min="0" step="1" value={form.max_limit} onChange={(e) => setForm({ ...form, max_limit: e.target.value })} className={inputCls} /></div>
          </div>
          <DialogFooter><Button data-testid="save-branch-button" onClick={save} disabled={busy} className={primaryBtn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (dlg?.mode === "edit" ? "Save Changes" : "Create Branch")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle>Delete Branch</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Permanently delete <span className="font-semibold">{delTarget?.email}</span>? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" className={outlineBtn} onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button data-testid="confirm-delete-branch-button" onClick={del} className="bg-rose-600 hover:bg-rose-700 text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) { setPwTarget(null); setNewPw(""); } }}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle>Reset Password · {pwTarget?.name || pwTarget?.email}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">New temporary password</Label>
            <Input data-testid="branch-reset-pw-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inputCls} />
            <p className="text-xs text-slate-400">Min 8 chars incl. uppercase, lowercase, number and symbol. The branch will be required to set their own password on next login.</p>
          </div>
          <DialogFooter><Button data-testid="confirm-reset-branch-pw-button" onClick={resetPw} disabled={busy} className={primaryBtn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Merchants ---------------- */
function MerchantsTab({ merchants, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", login_email: "", login_password: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [logsFor, setLogsFor] = useState(null);
  const [logs, setLogs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [connectFor, setConnectFor] = useState(null);
  const [connIds, setConnIds] = useState([]);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data.filter((b) => b.role === "branch"))).catch(() => {});
  }, []);

  const openConnect = (v) => { setConnectFor(v); setConnIds(v.branch_ids || []); };
  const saveConnect = async () => {
    try {
      await api.put(`/merchants/${connectFor.id}/branches`, { branch_ids: connIds });
      toast.success("Branch connections updated"); setConnectFor(null); reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const create = async () => {
    if (!form.name || !form.login_email || !form.login_password) return toast.error("Name, email and password required");
    setBusy(true);
    try {
      const { data } = await api.post("/merchants", form);
      setCreated(data); setForm({ name: "", login_email: "", login_password: "" }); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggle = async (v) => {
    try {
      await api.post(`/merchants/${v.id}/${v.status === "active" ? "suspend" : "reactivate"}`);
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const viewLogs = async (v) => {
    setLogsFor(v);
    try { const { data } = await api.get(`/merchants/${v.id}/deliveries`); setLogs(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const resetPw = async () => {
    if (!newPw) return toast.error("Enter a new password");
    setBusy(true);
    try {
      await api.post(`/merchants/${pwTarget.id}/reset-password`, { new_password: newPw });
      toast.success("Password reset. Merchant must change it on next login.");
      setPwTarget(null); setNewPw("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const reset2fa = async (v) => {
    try { await api.post(`/merchants/${v.id}/reset-2fa`); toast.success("2FA reset — merchant can set it up again"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className={`${card} p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900">Merchants</h2>
          <p className="text-sm text-slate-500">Create merchant accounts for automated API submissions &amp; webhooks. Assign QRs to a merchant from the QR Management tab.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreated(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-merchant-button" className={primaryBtn}><Plus className="w-4 h-4 mr-1" />Add Merchant</Button>
          </DialogTrigger>
          <DialogContent className={dialogCls}>
            <DialogHeader><DialogTitle>{created ? "Merchant Created" : "Add Merchant"}</DialogTitle></DialogHeader>
            {created ? (
              <div className="space-y-3 text-sm">
                <p className="text-slate-500">Share these with your merchant. The API key &amp; secret are shown only once.</p>
                {[["Merchant Code", created.merchant_code], ["Login Email", created.login_email], ["API Key", created.api_key], ["Webhook Secret", created.webhook_secret]].map(([l, v]) => (
                  <div key={l}>
                    <Label className="text-xs text-slate-500">{l}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className={`flex-1 ${fieldWell} px-2 py-1.5 text-xs font-mono break-all text-slate-700`}>{v}</code>
                      <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => { navigator.clipboard.writeText(v); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                <div><Label className="text-xs text-slate-500">Merchant Name</Label><Input data-testid="merchant-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
                <div><Label className="text-xs text-slate-500">Merchant Login Email</Label><Input data-testid="merchant-email-input" value={form.login_email} onChange={(e) => setForm({ ...form, login_email: e.target.value })} className={inputCls} /></div>
                <div><Label className="text-xs text-slate-500">Merchant Login Password</Label><Input data-testid="merchant-password-input" value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} className={inputCls} /></div>
              </div>
            )}
            {!created && <DialogFooter><Button data-testid="save-merchant-button" onClick={create} disabled={busy} className={primaryBtn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Merchant"}</Button></DialogFooter>}
          </DialogContent>
        </Dialog>
      </div>

      <MerchantTools merchants={merchants}>{(mShown) => (
      <div className="overflow-x-auto mt-3">
        <table className="w-full min-w-[820px]">
          <thead><tr className="border-b border-slate-200"><Th>Merchant</Th><Th>Connected Branches</Th><Th>Usable QRs</Th><Th>API Key</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
          <tbody>
            {mShown.map((v) => (
              <tr key={v.id} className="border-b border-slate-100" data-testid={`merchant-row-${v.merchant_code}`}>
                <Td><div className="font-medium text-slate-800">{v.name}</div><div className="text-xs text-slate-400 font-mono">{v.merchant_code}</div></Td>
                <Td>{v.connected_branches?.length ? <span className="text-xs text-slate-600">{v.connected_branches.join(", ")}</span> : <span className="text-slate-400">none</span>}</Td>
                <Td>{v.assigned_qrs?.length ? v.assigned_qrs.join(", ") : <span className="text-slate-400">none</span>}</Td>
                <Td mono>{v.api_key_masked}</Td>
                <Td>{v.status === "active"
                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">Active</span>
                  : <span className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200">Suspended</span>}</Td>
                <Td>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => openConnect(v)} data-testid={`connect-merchant-${v.merchant_code}`}>Connect</Button>
                    <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => toggle(v)} data-testid={`toggle-merchant-${v.merchant_code}`}>{v.status === "active" ? "Suspend" : "Reactivate"}</Button>
                    <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => { setPwTarget(v); setNewPw(""); }} data-testid={`reset-merchant-pw-${v.merchant_code}`} title="Reset password"><KeyRound className="w-3.5 h-3.5" /></Button>
                    {v.totp_enabled && <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => reset2fa(v)} data-testid={`reset-merchant-2fa-${v.merchant_code}`} title="Reset 2FA"><ShieldOff className="w-3.5 h-3.5" /></Button>}
                    <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => viewLogs(v)} data-testid={`merchant-logs-${v.merchant_code}`}>Logs</Button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}</MerchantTools>

      <Dialog open={!!connectFor} onOpenChange={(o) => !o && setConnectFor(null)}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle>Connect Branches · {connectFor?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">This merchant can use all QR codes of the branches you connect here.</p>
          <div className="mt-2 space-y-1.5 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
            {branches.length === 0 && <p className="text-xs text-slate-400">No branches yet.</p>}
            {branches.map((b) => (
              <label key={b.id} data-testid={`connect-branch-opt-${b.email}`} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="accent-teal-600" checked={connIds.includes(b.id)} onChange={(e) => setConnIds(e.target.checked ? [...connIds, b.id] : connIds.filter((x) => x !== b.id))} />
                <span>{b.name || b.email} <span className="text-slate-400 text-xs">{b.email}</span></span>
              </label>
            ))}
          </div>
          <DialogFooter><Button data-testid="save-connect-branches-button" onClick={saveConnect} className={primaryBtn}>Save Connections</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!logsFor} onOpenChange={(o) => !o && setLogsFor(null)}>
        <DialogContent className={`${dialogCls} max-w-2xl`}>
          <DialogHeader><DialogTitle>Webhook Deliveries · {logsFor?.merchant_code}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {logs.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No deliveries yet.</p> : (
              <table className="w-full"><thead><tr className="border-b border-slate-200"><Th>Event</Th><Th>Status</Th><Th>Attempts</Th><Th>Code</Th><Th>When</Th></tr></thead>
                <tbody>{logs.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100">
                    <Td mono>{l.event_type}</Td>
                    <Td><DeliveryBadge s={l.status} /></Td>
                    <Td>{l.attempt_count}</Td>
                    <Td>{l.response_code || "—"}</Td>
                    <Td>{timeAgo(l.last_attempted_at || l.created_at)}</Td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) { setPwTarget(null); setNewPw(""); } }}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle>Reset Password · {pwTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">New temporary password</Label>
            <Input data-testid="merchant-reset-pw-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inputCls} />
            <p className="text-xs text-slate-400">Min 8 chars incl. uppercase, lowercase, number and symbol. The merchant will be required to set their own password on next login.</p>
          </div>
          <DialogFooter><Button data-testid="confirm-reset-merchant-pw-button" onClick={resetPw} disabled={busy} className={primaryBtn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MerchantTools({ merchants, children }) {
  const mRow = (v) => [v.name || "", v.merchant_code, (v.connected_branches || []).join("; "), (v.assigned_qrs || []).join("; "), v.api_key_masked || "", v.status];
  const [q, setQ, shown] = useTableSearch(merchants, mRow);
  return (
    <>
      <div className="mt-4 flex justify-end"><TableTools id="merchants" query={q} onQuery={setQ} cols={["Merchant", "Code", "Connected Branches", "Usable QRs", "API Key", "Status"]} rows={shown.map(mRow)} title="ABCD Pay — Merchants" filename="abcdpay-merchants" /></div>
      {children(shown)}
    </>
  );
}

function EditQrDialog({ biz, merchants, onClose, reload, isSuper }) {
  const [dailyLimit, setDailyLimit] = useState(biz.daily_limit != null ? String(biz.daily_limit) : "");
  const [lifetimeLimit, setLifetimeLimit] = useState(biz.lifetime_limit != null ? String(biz.lifetime_limit) : "");
  const [amountMin, setAmountMin] = useState(String(biz.amount_min ?? ""));
  const [amountMax, setAmountMax] = useState(String(biz.amount_max ?? ""));
  const [googleEmail, setGoogleEmail] = useState(biz.google_account_email || "");
  const [googleName, setGoogleName] = useState(biz.google_business_name || "");
  const [googlePhone, setGooglePhone] = useState(biz.google_business_phone || "");
  const [bankAcc, setBankAcc] = useState(biz.bank_account_number || "");
  const [bankIfsc, setBankIfsc] = useState(biz.bank_ifsc || "");
  const [bankName, setBankName] = useState(biz.bank_name || "");
  const [paymentProvider, setPaymentProvider] = useState(biz.payment_provider || "");
  const [merchantIds, setMerchantIds] = useState((biz.merchants || []).map((v) => v.id));
  const [qrImage, setQrImage] = useState(biz.qr_image || "");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/branch/upload-image", fd);
      setQrImage(data.url);
      toast.success("New QR image uploaded");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const save = async () => {
    const amin = parseFloat(amountMin), amax = parseFloat(amountMax);
    if (isNaN(amin) || isNaN(amax) || amin < 1 || amax > 100000 || amin > amax) return toast.error("Amount must be between ₹1 and ₹1,00,000");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((googleEmail || "").trim())) return toast.error("Email is required and must be valid");
    if (!(googleName || "").trim()) return toast.error("Business Name / Account Name is required");
    const mErr = mobileError(googlePhone);
    if (mErr) return toast.error(mErr);
    if (!qrImage) return toast.error("QR image is required. Please upload the QR image");
    const bErr = bankError(bankAcc, bankIfsc, bankName);
    if (bErr) return toast.error(bErr);
    if (!paymentProvider) return toast.error("Please select a Payment App");
    const dl = limitVal(dailyLimit), ll = limitVal(lifetimeLimit);
    if (badLimit(dl) || badLimit(ll)) return toast.error("Limits must be a positive amount or blank");
    setBusy(true);
    try {
      const body = { name: biz.name, amount_min: amin, amount_max: amax, merchant_ids: merchantIds, google_account_email: googleEmail.trim(), google_business_name: googleName.trim(), google_business_phone: googlePhone.trim(), payment_provider: paymentProvider, daily_limit: dl, lifetime_limit: ll, bank_account_number: bankAcc.trim(), bank_ifsc: bankIfsc.trim().toUpperCase(), bank_name: bankName.trim() };
      if (qrImage && qrImage !== biz.qr_image) body.qr_image = qrImage;
      await api.put(`/businesses/${biz.id}`, body);
      toast.success("QR updated"); reload(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={dialogCls}>
        <DialogHeader><DialogTitle>Edit QR · {biz.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">QR Image <span className="text-rose-500">*</span></Label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="edit-qr-image-input" />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="edit-qr-image-button" className="mt-1 w-full border-2 border-dashed border-slate-300 rounded-lg p-4 flex flex-col items-center gap-2 hover:border-teal-400 transition-colors">
              {qrImage ? <img src={fileUrl(qrImage)} alt="qr" className="w-24 h-24 object-contain rounded bg-white p-1 border border-slate-200" /> : (uploading ? <Loader2 className="w-5 h-5 animate-spin text-teal-600" /> : <UploadCloud className="w-5 h-5 text-teal-600" />)}
              <span className="text-xs text-slate-500">{uploading ? "Uploading…" : "Re-upload QR image"}</span>
            </button>
            <div className={`flex items-center gap-2 mt-2 p-2 rounded-lg ${fieldWell}`}>
              <div className="text-xs">
                <span className="text-slate-400">UPI ID (locked): </span>
                <span className="font-mono text-slate-700">{biz.upi_id}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs text-slate-500">Email <span className="text-rose-500">*</span></Label><Input data-testid="edit-google-email-input" type="email" placeholder="business@gmail.com" value={googleEmail} onChange={(e) => setGoogleEmail(e.target.value)} className={inputCls} /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-500">Business Name / Account Name <span className="text-rose-500">*</span></Label><Input data-testid="edit-google-name-input" value={googleName} onChange={(e) => setGoogleName(e.target.value)} className={inputCls} /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-500">Bank Name <span className="text-rose-500">*</span></Label><Input data-testid="edit-bank-name-input" placeholder="HDFC Bank" value={bankName} onChange={(e) => setBankName(e.target.value)} className={inputCls} /></div>
            <div><Label className="text-xs text-slate-500">Account Number <span className="text-rose-500">*</span></Label><Input data-testid="edit-account-number-input" inputMode="numeric" value={bankAcc} onChange={(e) => setBankAcc(e.target.value)} className={`${inputCls} font-mono`} /></div>
            <div><Label className="text-xs text-slate-500">IFSC Code <span className="text-rose-500">*</span></Label><Input data-testid="edit-ifsc-input" placeholder="HDFC0001234" maxLength={11} value={bankIfsc} onChange={(e) => setBankIfsc(e.target.value.toUpperCase())} className={`${inputCls} font-mono`} /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-500">Mobile Number <span className="text-rose-500">*</span></Label><Input data-testid="edit-google-phone-input" placeholder="+91 96924 81524" value={googlePhone} onChange={(e) => setGooglePhone(e.target.value)} className={inputCls} /></div>
            <div className="col-span-2"><Label className="text-xs text-slate-500">Payment App <span className="text-rose-500">*</span></Label>
              <Select value={paymentProvider} onValueChange={setPaymentProvider}>
                <SelectTrigger data-testid="edit-payment-provider-select" className="mt-1 bg-white border-slate-300 text-slate-900"><SelectValue placeholder="Select payment app" /></SelectTrigger>
                <SelectContent className={dialogCls}>
                  <SelectItem value="gpay">Google Pay</SelectItem>
                  <SelectItem value="bharatpe">Bharat Pay</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500">Daily Limit (₹) <span className="text-slate-400">blank = no limit</span></Label><Input data-testid="edit-daily-limit-input" type="number" min="1" placeholder="No limit" value={dailyLimit} onChange={(e) => setDailyLimit(e.target.value)} className={inputCls} /></div>
            <div><Label className="text-xs text-slate-500">Lifetime Limit (₹) <span className="text-slate-400">blank = no limit</span></Label><Input data-testid="edit-lifetime-limit-input" type="number" min="1" placeholder="No limit" value={lifetimeLimit} onChange={(e) => setLifetimeLimit(e.target.value)} className={inputCls} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="hidden"><Label className="text-xs text-slate-500">Amount Min (₹)</Label><Input data-testid="edit-amount-min-input" value={amountMin} onChange={(e) => setAmountMin(e.target.value)} className={inputCls} /></div>
            <div className="hidden"><Label className="text-xs text-slate-500">Amount Max (₹)</Label><Input data-testid="edit-amount-max-input" value={amountMax} onChange={(e) => setAmountMax(e.target.value)} className={inputCls} /></div>
          </div>
          {isSuper && (
          <div>
            <Label className="text-xs text-slate-500">Assigned Merchants</Label>
            <div data-testid="edit-merchant-select" className="mt-1 space-y-1.5 max-h-32 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
              {merchants.length === 0 && <p className="text-xs text-slate-400">No merchants yet.</p>}
              {merchants.map((v) => (
                <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" className="accent-teal-600" checked={merchantIds.includes(v.id)} onChange={(e) => setMerchantIds(e.target.checked ? [...merchantIds, v.id] : merchantIds.filter((x) => x !== v.id))} />
                  <span>{v.name || v.merchant_code} <span className="text-slate-400 font-mono text-xs">{v.merchant_code}</span></span>
                </label>
              ))}
            </div>
          </div>
          )}
        </div>
        <DialogFooter><Button data-testid="save-edit-qr-button" onClick={save} disabled={busy} className={primaryBtn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsTab() {
  const [keys, setKeys] = useState([]);
  const [branches, setBranches] = useState([]);
  const [dlg, setDlg] = useState(null); // null | {mode:"create"} | {mode:"edit", key}
  const [name, setName] = useState("");
  const [branchId, setBranchId] = useState("");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/branch/ext-keys").then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data.filter((b) => b.role === "branch"))).catch(() => {});
  }, []);

  const openCreate = () => { setName(""); setBranchId(""); setCreated(null); setDlg({ mode: "create" }); };
  const openEdit = (k) => { setName(k.name); setBranchId(k.branch_id || ""); setCreated(null); setDlg({ mode: "edit", key: k }); };

  const save = async () => {
    if (!branchId) return toast.error("Select a branch");
    setBusy(true);
    try {
      if (dlg.mode === "create") {
        if (!name) return toast.error("Name required");
        const { data } = await api.post("/branch/ext-keys", { name, branch_id: branchId });
        setCreated(data); load();
      } else {
        await api.put(`/branch/ext-keys/${dlg.key.id}`, { branch_id: branchId });
        toast.success("Branch updated"); setDlg(null); load();
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const toggleStatus = async (k) => {
    try { await api.post(`/branch/ext-keys/${k.id}/${k.status === "active" ? "revoke" : "activate"}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className={`${card} p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold tracking-tight text-slate-900">Extension Access</h2>
          <p className="text-sm text-slate-500">Chrome-extension keys issued <b>per Branch</b> — a key can write payments to all of its branch's QRs. Managed by Super Admin only; the key is shown once.</p>
        </div>
        <Button data-testid="add-ext-key-button" onClick={openCreate} className={primaryBtn}><Plus className="w-4 h-4 mr-1" />Generate Key</Button>
      </div>
      <div className="overflow-x-auto mt-4">
        <table className="w-full min-w-[760px]">
          <thead><tr className="border-b border-slate-200"><Th>Name</Th><Th>Key</Th><Th>Branch</Th><Th>Status</Th><Th>Created</Th><Th>Actions</Th></tr></thead>
          <tbody>
            {keys.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">No keys yet.</td></tr>}
            {keys.map((k) => (
              <tr key={k.id} className="border-b border-slate-100" data-testid={`ext-key-row-${k.id}`}>
                <Td>{k.name}</Td>
                <Td mono>{k.key_masked}</Td>
                <Td><span className="text-xs text-slate-600">{k.branch_name || <span className="text-slate-400">unassigned</span>}</span></Td>
                <Td>{k.status === "active"
                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">Active</span>
                  : <span className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200">Revoked</span>}</Td>
                <Td>{timeAgo(k.created_at)}</Td>
                <Td><div className="flex gap-2">
                  <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => openEdit(k)} data-testid={`edit-ext-key-${k.id}`}>Branch</Button>
                  <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => toggleStatus(k)} data-testid={`toggle-ext-key-${k.id}`}>{k.status === "active" ? "Revoke" : "Activate"}</Button>
                </div></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle>{dlg?.mode === "edit" ? "Change Branch" : (created ? "Key Generated" : "Generate Extension Key")}</DialogTitle></DialogHeader>
          {created ? (
            <div className="space-y-2 text-sm">
              <p className="text-slate-500">Copy this key now — it won't be shown again.</p>
              <div className="flex items-center gap-2">
                <code data-testid="ext-key-value" className={`flex-1 ${fieldWell} px-2 py-1.5 text-xs font-mono break-all text-slate-700`}>{created.api_key}</code>
                <Button size="sm" variant="outline" className={`${outlineBtn} h-8`} onClick={() => { try { navigator.clipboard.writeText(created.api_key); toast.success("Copied"); } catch (e) { toast.error("Copy manually"); } }}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {dlg?.mode === "create" && <div><Label className="text-xs text-slate-500">Key Name</Label><Input data-testid="ext-key-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></div>}
              <div>
                <Label className="text-xs text-slate-500">Branch</Label>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger data-testid="ext-key-branch-select" className="mt-1 bg-white border-slate-300 text-slate-900"><SelectValue placeholder="Select a branch" /></SelectTrigger>
                  <SelectContent className={dialogCls}>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name || b.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {!created && <DialogFooter><Button data-testid="save-ext-key-button" onClick={save} disabled={busy} className={primaryBtn}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (dlg?.mode === "edit" ? "Save" : "Generate")}</Button></DialogFooter>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DeliveryBadge({ s }) {
  const map = {
    delivered: "bg-teal-50 text-teal-700 border-teal-200",
    failed: "bg-rose-50 text-rose-600 border-rose-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${map[s] || map.pending}`}>{s}</span>;
}
